import { Link, Navigate, Outlet, useLocation } from 'react-router';

import { sessionEndedBySignOut } from './session';
import { useAuth } from './useAuth';

/** Field mode keeps its queued work on the device, so it is the one branch that opens offline. */
const OFFLINE_CAPABLE_PREFIX = '/field';

function OfflineNotice({ canUseField }: { canUseField: boolean }): React.JSX.Element {
  return (
    <main className="fo-page" id="main">
      <h1>You’re offline</h1>
      <p role="status">
        FieldOps can’t reach the server, so this page can’t load. It will reconnect on its own when
        the signal comes back.
      </p>
      {canUseField ? (
        <p>
          <Link to={OFFLINE_CAPABLE_PREFIX}>Open field mode</Link> to see the work saved on this
          device. It is sent as soon as you are back online.
        </p>
      ) : null}
    </main>
  );
}

/**
 * Guards a branch of the route tree.
 *
 * While the session is still being resolved the guard renders nothing rather than redirecting,
 * because redirecting on a not-yet-known answer would bounce a signed-in user to the login screen
 * on every page reload. Offline, only field mode renders; everything else explains why it cannot.
 *
 * The guard is a convenience for the person using the product, never a security boundary. It
 * decides what to render; the server decides what is permitted, and refuses independently.
 *
 * @param permission a capability the caller must hold to reach this branch
 * @returns the guarded routes, a redirect, or nothing while resolving
 */
export function RequireAuth({ permission }: { permission?: string }): React.JSX.Element | null {
  const { status, can } = useAuth();
  const location = useLocation();

  if (status === 'resolving') {
    return null;
  }

  if (status === 'anonymous') {
    // The attempted location travels with the redirect so signing in returns the user to where
    // they were going rather than dumping them on a dashboard. Not after a deliberate sign-out,
    // though: on a shared device the next person to sign in would land on the previous one's record.
    const from = sessionEndedBySignOut()
      ? undefined
      : { from: location.pathname + location.search + location.hash };
    return <Navigate to="/sign-in" replace state={from} />;
  }

  if (status === 'offline' && !location.pathname.startsWith(OFFLINE_CAPABLE_PREFIX)) {
    return <OfflineNotice canUseField={can('JOB_FIELD_EXECUTE')} />;
  }

  if (permission !== undefined && !can(permission)) {
    return <Navigate to="/not-permitted" replace state={{ permission }} />;
  }

  return <Outlet />;
}
